import { ReactNode, Suspense } from 'react';

import { Button, textStyles } from '@/ui';
import type { WidthOptions } from '@types';
import clsx from 'clsx';
import { formatInTimeZone } from 'date-fns-tz';
import he from 'he';
import parse, { DOMNode, Element, HTMLReactParserOptions, domToReact } from 'html-react-parser';

import { SearchParamReplacer } from './search-param-replacer';

type Variants = 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';

const buttonVariantLookup: Record<string, Variants> = {
  primary: 'default',
  secondary: 'secondary',
  text: 'link',
  ghost: 'ghost',
  destructive: 'destructive',
};

// Helper function to safely parse iframe content
const createIframe = (src: string, width: string, height: string, frameborder: string, allowfullscreen: boolean) => {
  return <iframe src={src} width={width} height={height} frameBorder={frameborder} allowFullScreen={allowfullscreen} />;
};

const options: HTMLReactParserOptions = {
  replace(domNode) {
    if (domNode instanceof Element && domNode.attribs) {
      switch (domNode.name) {
        case 'button':
          return (
            <Button
              variant={buttonVariantLookup[domNode.attribs['data-button-variant']] || 'default'}
              className={`mb-4 max-w-min whitespace-nowrap ${
                domNode.attribs['data-style-alignment'] === 'center' && 'mx-auto! flex!'
              }`}
              href={domNode.attribs['data-href'] || ''}
              target={domNode.attribs['data-href-external'] === 'true' ? '_blank' : undefined}
            >
              {domNode.attribs.label}
            </Button>
          );
        case 'div':
          // Handle our pre-processed iframe divs
          if (domNode.attribs['data-iframe'] === 'true') {
            return createIframe(
              domNode.attribs['data-src'],
              domNode.attribs['data-width'],
              domNode.attribs['data-height'],
              domNode.attribs['data-frameborder'],
              domNode.attribs['data-allowfullscreen'] === 'true',
            );
          }
          break;
        case 'p':
          // Special handling for p tags that might contain iframe code
          const children = domNode.children as DOMNode[];
          const content = domToReact(children, options);

          return content;
        case 'span':
          if (domNode.attribs['data-searchparamname']) {
            const paramName = domNode.attribs['data-searchparamname'];
            const fallback = domNode.attribs['data-searchparamfallback'];
            let defaultValue = '';

            if (domNode.children.length > 0) {
              const firstChild = domNode.children[0];
              if ('data' in firstChild) {
                defaultValue = firstChild.data || '';
              }
            }

            return (
              // suspense added for search params
              <Suspense>
                <SearchParamReplacer paramName={paramName} fallback={fallback} defaultValue={defaultValue} />
              </Suspense>
            );
          }
        case 'customtag':
          if (domNode.attribs['data-tag'] === 'currentDate') {
            const currentDate = new Date();
            return <span>{formatInTimeZone(currentDate, 'Europe/London', 'EEEE do MMMM')}</span>;
          }
      }
    }
  },
};

interface RichTextParserProps {
  children: ReactNode;
  className?: string;
  blockWidth?: WidthOptions;
}

export function RichText({ children, className, blockWidth }: RichTextParserProps) {
  if (typeof children === 'string') {
    // First decode any HTML entities
    const decodedContent = he.decode(children);

    // Pre-process the content to handle problematic iframe tags
    // This converts any raw iframe HTML text into properly parsed elements
    const processedContent = decodedContent.replace(
      /<p>(\s*)<iframe(.+?)<\/iframe>(\s*)<\/p>/g,
      (match, leadingSpace, iframeContent) => {
        // Extract the attributes from the iframe tag
        const srcMatch = iframeContent.match(/src="([^"]+)"/);
        const widthMatch = iframeContent.match(/width="([^"]+)"/);
        const heightMatch = iframeContent.match(/height="([^"]+)"/);
        const frameborderMatch = iframeContent.match(/frameborder="([^"]+)"/);
        const allowfullscreenMatch = iframeContent.includes('allowfullscreen');

        if (srcMatch) {
          // Create a special marker that the parser will recognize
          return `<div data-iframe="true" data-src="${srcMatch[1]}" data-width="${widthMatch ? widthMatch[1] : '100%'}" data-height="${heightMatch ? heightMatch[1] : '450px'}" data-frameborder="${frameborderMatch ? frameborderMatch[1] : '0'}" data-allowfullscreen="${allowfullscreenMatch}"></div>`;
        }

        return match; // Return original if we couldn't parse it
      },
    );

    return (
      <div
        className={clsx(
          blockWidth ? textStyles.div.replace('!max-w-', 'max-w-') : textStyles.div,
          {
            'max-w-none!': blockWidth === 'full',
            'max-w-[theme(screens.3xl)]!': blockWidth === 'desktop',
            'max-w-(--breakpoint-2xl)!': blockWidth === 'wide',
            'max-w-(--breakpoint-xl)!': blockWidth === 'standard',
            'max-w-(--breakpoint-lg)!': blockWidth === 'narrow',
            'max-w-(--breakpoint-md)!': blockWidth === 'prose',
          },
          className,
        )}
      >
        {parse(processedContent, options)}
      </div>
    );
  }

  return <>{children}</>;
}
